import * as React from 'react';

import { createStyles, makeStyles } from '@material-ui/styles';
import * as classNames from 'classnames';
import { useApi } from '../@context/Api';
import { NavLink, Link } from 'react-router-dom';
import useReactRouter from 'use-react-router';
import mediaQueries from 'src/constants/responsive';
import spacing from 'src/constants/spacing';
import stylesheet, { secondaryColor, tertiaryColor } from 'src/constants/stylesheet';
import { Responsive } from 'src/@context/Responsive';
import { useState } from 'react';
import logo from '../Assets/roux_logo.svg';

export type MenuProps = {};

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      position: 'relative',
      display: 'flex',
      justifyContent: 'space-between',

      [mediaQueries.Mobile]: {
        paddingLeft: spacing.Gutter.Mobile,
        paddingRight: spacing.Gutter.Mobile,
        height: spacing.headerHeight.Mobile,
        paddingTop: 15,
        paddingBottom: 15,
      },
      [mediaQueries.AfterMobile]: {
        paddingTop: 15,
        paddingBottom: 8,
      },
      [mediaQueries.Tablet]: {
        paddingLeft: spacing.Gutter.Tablet,
        paddingRight: spacing.Gutter.Tablet,
        height: spacing.headerHeight.Tablet
      },
      [mediaQueries.Laptop]: {
        paddingLeft: spacing.Gutter.Laptop,
        paddingRight: spacing.Gutter.Laptop,
        height: spacing.headerHeight.Laptop
      },
      [mediaQueries.Desktop]: {
        paddingLeft: spacing.Gutter.Desktop,
        paddingRight: spacing.Gutter.Desktop,
        height: spacing.headerHeight.Desktop
      },
    },
    logoContainer: {
      position: 'relative',
      [mediaQueries.Mobile]: {
        width: 167,
      },
      [mediaQueries.AfterMobile]: {
        width: 250,
      },
    },
    logo: {
      width: '100%',
      height: 'auto',
      display: 'block',
    },
    navigation: {
      position: 'relative',
      paddingTop: 20,
      paddingBottom: 20,

    },
    link: {
      ...stylesheet.MediumText,
      marginLeft: 30,
      fontSize: '1.1rem',
      '&:hover': {
        color: tertiaryColor
      }
    },
    active: {
      color: secondaryColor
    },
    isOpen: {},
    burguerLink: {
      position: 'relative',
      width: 25,
      alignSelf: 'center',
      marginTop: -20,
      zIndex: 20,
      cursor: 'pointer',
    },
    burguerLogo: {
      display: 'block',
      position: 'absolute',
      left: 0,
      right: 0,
      backgroundColor: secondaryColor,
      height: 3,
      top: 8,
      transition: 'background 0.2s',

      '&:before': {
        content: '""',
        position: 'absolute',
        display: 'block',
        left: 0,
        width: '100%',
        backgroundColor: secondaryColor,
        height: 3,
        top: '-8px',

        transitionDuration: '0.2s, 0.2s',

        transitionProperty: 'top, transform',

        transitionDelay: '0.2s, 0s',

        transitionTimingFunction: 'cubic-bezier(0.04, 0.04, 0.12, 0.96)',
      },

      '&:after': {
        content: '""',
        position: 'absolute',
        display: 'block',
        left: 0,
        width: '100%',
        backgroundColor: secondaryColor,
        height: 3,
        bottom: '-8px',

        transitionDuration: '0.2s, 0.2s',
        transitionProperty: 'bottom, transform',
        transitionDelay: '0.2s, 0s',
        transitionTimingFunction: 'cubic-bezier(0.04, 0.04, 0.12, 0.96)',
      },

      '$isOpen &': {
        backgroundColor: 'transparent',
      },

      '$isOpen &:before': {
        top: 0,
        transform: 'rotate(45deg)',
        transitionDelay: '0s, 0.2s',
      },

      '$isOpen &:after': {
        bottom: 0,
        transform: 'rotate(-45deg)',
        transitionDelay: '0s, 0.2s',
      }

    },
    mobileNavigation: {
      position: 'fixed',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 10,

      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: '15vh 20px',
      background: 'white',

      opacity: 0,
      pointerEvents: 'none',
      transition: 'opacity .3s ease',

      '$isOpen &': {
        pointerEvents: 'initial',
        opacity: 1
      }
    },
    mobileLink: {
      fontSize: '2rem',
      margin: 10
    }
  }));

const Menu: React.FC<MenuProps> = () => {
  const cls = useStyles();
  const { app } = useApi();
  const [opened, setOpened] = useState(false);

  const { location: { pathname } } = useReactRouter();

  const onClick = () => {
    setOpened(!opened);
  }

  const handleMobileCLick = () => {
    if (opened) {
      setOpened(false);
    }
  }

  return (
    <header className={classNames(cls.root, opened && cls.isOpen)}>
      <Link 
        to='/'
        className={cls.logoContainer}
      >
        <img src={logo} className={cls.logo} alt="Roux ingenierie logo" />
      </Link>
      <Responsive steps={['AfterMobile']}>
        <nav className={cls.navigation}>
          {app && app.Menus.map(m => {
            switch (m.type) {
              case "Home":
                return <Link
                  key={m.UID}
                  to='/'
                  className={classNames(cls.link, pathname.split('/')[1] === "" ? cls.active : undefined)}
                >
                  {m.Title}
                </Link>
              default:
                return <NavLink
                  key={m.UID}
                  to={`/${m.UID}`}
                  className={cls.link}
                  activeClassName={cls.active}
                >
                  {m.Title}
                </NavLink>
            }
          })}
        </nav>
      </Responsive>

      <Responsive steps={['Mobile']}>
        <a className={cls.burguerLink} onClick={onClick}>
          <span className={cls.burguerLogo} />
        </a>
        <nav className={cls.mobileNavigation}>
          {app && app.Menus.map(m => {
            switch (m.type) {
              case "Home":
                return <Link
                  key={m.UID}
                  to='/'
                  onClick={handleMobileCLick}
                  className={classNames(cls.mobileLink, pathname.split('/')[1] === "" ? cls.active : undefined)}
                >
                  {m.Title}
                </Link>
              default:
                return <NavLink
                  key={m.UID}
                  to={`/${m.UID}`}
                  className={cls.mobileLink}
                  activeClassName={cls.active}
                  onClick={handleMobileCLick}
                >
                  {m.Title}
                </NavLink>
            }
          })}
        </nav>
      </Responsive>
    </header>
  );
}

export default Menu;



// WEBPACK FOOTER //
// ./src/Components/Menu.tsx